if ( !customElements.get( 'video-element' ) )
{
	class VideoElement extends HTMLElement {
		constructor() {
			super();

			if (this.posterElement) {
				this.posterElement.addEventListener('click', this.handlePosterClick.bind(this));
			}

			if (this.autoplay) {
				FoxTheme.Motion.inView(this, () => {
					if (!this.paused) {
						this.play();
					}

					return () => {
						this.pause();
					};
				});
			}
		}

		get posterElement() {
			return this.querySelector('[id^="DeferredPoster-"]');
		}

		get controlledElement() {
			return this.hasAttribute('aria-controls') ? document.getElementById(this.getAttribute('aria-controls')) : null;
		}

		get autoplay() {
			return this.hasAttribute('autoplay');
		}

		get playing() {
			return this.hasAttribute('playing');
		}

		get player() {
			return (this.playerProxy =
				this.playerProxy ||
				new Proxy(this.initializePlayer(), {
					get: (target, prop) => {
						return async () => {
							target = await target;
							this.handlePlayerAction(target, prop);
						};
					},
				}));
		}

		static get observedAttributes() {
			return ['playing'];
		}

		handlePosterClick(event) {
			event.preventDefault();
			event.stopPropagation();

			if (this.playing) {
				this.paused = true;
				this.pause();
			} else {
				this.paused = false;
				this.play();
			}
		}

		play() {
			if (!this.playing) {
				this.player.play();
			}
		}

		pause() {
			if (this.playing) {
				this.player.pause();
			}
		}

		attributeChangedCallback(name, oldValue, newValue) {
			if (name === 'playing') {
				if (oldValue === null && newValue === '') {
					return this.dispatchEvent(new CustomEvent('video:play', { bubbles: true }));
				}

				if (newValue === null) {
					return this.dispatchEvent(new CustomEvent('video:pause', { bubbles: true }));
				}
			}
		}

		initializePlayer() {
			if (this.hasAttribute('source')) {
				this.setAttribute('loaded', '');
				this.closest('.media')?.classList.remove('loading');

				return new Promise(async (resolve) => {
					const templateElement = this.querySelector('template');
					if (templateElement) {
						templateElement.replaceWith(templateElement.content.firstElementChild.cloneNode(true));
					}
					const muteVideo = this.hasAttribute('autoplay') || window.matchMedia('screen and (max-width: 1023px)').matches;
					const script = document.createElement('script');
					script.type = 'text/javascript';
					if (this.getAttribute('source') === 'youtube') {
						if (!window.YT || !window.YT.Player) {
							script.src = 'https://www.youtube.com/iframe_api';
							document.head.appendChild(script);
							await new Promise((resolve2) => {
								script.onload = resolve2;
							});
						}
						await onYouTubeApiLoaded;
						const player = new YT.Player(this.querySelector('iframe'), {
							events: {
								onReady: () => {
									if (muteVideo) {
										player.mute();
									}
									resolve(player);
								},
								onStateChange: (event) => {
									if (event.data === YT.PlayerState.PLAYING) {
										this.setAttribute('playing', '');
									} else if (event.data === YT.PlayerState.ENDED || event.data === YT.PlayerState.PAUSED) {
										this.removeAttribute('playing');
									}
								},
							},
						});
					}
					if (this.getAttribute('source') === 'vimeo') {
						if (!window.Vimeo || !window.Vimeo.Player) {
							script.src = 'https://player.vimeo.com/api/player.js';
							document.head.appendChild(script);
							await new Promise((resolve2) => {
								script.onload = resolve2;
							});
						}
						const player = new Vimeo.Player(this.querySelector('iframe'));
						if (muteVideo) {
							player.setMuted(true);
						}
						player.on('play', () => {
							this.setAttribute('playing', '');
						});
						player.on('pause', () => this.removeAttribute('playing'));
						player.on('ended', () => this.removeAttribute('playing'));
						resolve(player);
					}
				});
			} else {
				this.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));
				this.setAttribute('loaded', '');
				this.closest('.media')?.classList.remove('loading');

				const player = this.querySelector('video');
				player.addEventListener('play', () => {
					this.setAttribute('playing', '');
					this.removeAttribute('suspended');
				});
				player.addEventListener('pause', () => {
					if (!player.seeking && player.paused) {
						this.removeAttribute('playing');
					}
				});
				return player;
			}
		}

		handlePlayerAction(target, prop) {
			if (this.getAttribute('source') === 'youtube') {
				prop === 'play' ? target.playVideo() : target.pauseVideo();
			} else {
				if (prop === 'play' && !this.hasAttribute('source')) {
					target.play().catch((error) => {
						if (error.name === 'NotAllowedError') {
							this.setAttribute('suspended', '');
							target.controls = true;
							const replacementImageSrc = target.previousElementSibling?.currentSrc;
							if (replacementImageSrc) {
								target.poster = replacementImageSrc;
							}
						}
					});
				} else {
					target[prop]();
				}
			}
		}
	}
	customElements.define('video-element', VideoElement);
}